import { useEffect, useRef, useState } from "react";
import CrossSellSetupResponse, {
  CrossSellVenue,
  WidgetThemeStyles,
} from "../domain_objects/CrossSellSetupResponse";
import WidgetService from "../services/WidgetService";
import { CrossSellVenueAndAvailability, State } from "../types";
import { ReloadContactDetails } from "../types/ReloadContactDetails";
import { WidgetConfiguratorStyles } from "../types/styles/WidgetConfiguratorStyles";
import {
  bookingDetails,
  widgetConfiguratorAvailabilitySearchResponses,
} from "../mocks";
import { AvailabilityType } from "../enums/AvailbilityType";

type WidgetConfiguratorMessageHandlerProps = {
  state: State;
  setState: (payload: Partial<State>) => void;
};

const WidgetConfiguratorMessageHandler: React.FC<
  WidgetConfiguratorMessageHandlerProps
> = ({ state, setState }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isListenerAttached, setIsListenerAttached] = useState(false);
  const widgetService = new WidgetService();

  const updateCSS = (css: string) => {
    const widgetConfiguratorStyles: WidgetConfiguratorStyles = JSON.parse(css);
    const themeStyles: WidgetThemeStyles = {
      ThemeId: state.themeId!,
      OverallGeneralBackgroundColour:
        widgetConfiguratorStyles.OverallLookViewModel.GeneralBackgroundColour,
      OverallGeneralTextColour:
        widgetConfiguratorStyles.OverallLookViewModel.GeneralTextColour,
      OverallGeneralTableReturnNoticeColour:
        widgetConfiguratorStyles.OverallLookViewModel
          .GeneralTableReturnNoticeColour,
      OverallGeneralBorderRadius:
        widgetConfiguratorStyles.OverallLookViewModel.GeneralBorderRadius,
      OverallGeneralFont:
        widgetConfiguratorStyles.OverallLookViewModel.GeneralFont,
      OverallBookButtonColour:
        widgetConfiguratorStyles.OverallLookViewModel.BookButtonColour,
      OverallBookButtonFontColour:
        widgetConfiguratorStyles.OverallLookViewModel.BookButtonFontColour,
      OverallResDiaryLogo:
        widgetConfiguratorStyles.OverallLookViewModel.ResDiaryLogo,
      OverallMessageBackgroundColour:
        widgetConfiguratorStyles.OverallLookViewModel
          .OverallMessageBackgroundColour,
      OverallMessageTextColour:
        widgetConfiguratorStyles.OverallLookViewModel.OverallMessageTextColour,
      BookingSummaryBackgroundColour:
        widgetConfiguratorStyles.BookingSummaryViewModel.BackgroundColour,
      BookingSummaryTextColour:
        widgetConfiguratorStyles.BookingSummaryViewModel.TextColour,
      BookingSummaryBorderRadius:
        widgetConfiguratorStyles.BookingSummaryViewModel.BorderRadius,
      CalendarBackgroundColour:
        widgetConfiguratorStyles.CalendarViewModel.BackgroundColour,
      CalendarBorderColour:
        widgetConfiguratorStyles.CalendarViewModel.BorderColour,
      CalendarMonthDayTextColour:
        widgetConfiguratorStyles.CalendarViewModel.MonthDayTextColour,
      CalendarBorderRadius:
        widgetConfiguratorStyles.CalendarViewModel.BorderRadius,
      CalendarDayTextColour:
        widgetConfiguratorStyles.CalendarViewModel.DayTextColour,
      CalendarDayBackgroundColour:
        widgetConfiguratorStyles.CalendarViewModel.DayBackgroundColour,
      CalendarDayBorderRadius:
        widgetConfiguratorStyles.CalendarViewModel.DayBorderRadius,
      CalendarSelectedTextColour:
        widgetConfiguratorStyles.CalendarViewModel.SelectedTextColour,
      CalendarSelectedBackgroundColour:
        widgetConfiguratorStyles.CalendarViewModel.SelectedBackgroundColour,
      DropdownGeneralBackgroundColour:
        widgetConfiguratorStyles.DropdownInputViewModel.GeneralBackgroundColour,
      DropdownGeneralTextColour:
        widgetConfiguratorStyles.DropdownInputViewModel.GeneralTextColour,
      DropdownGeneralValidationErrorColour:
        widgetConfiguratorStyles.DropdownInputViewModel
          .GeneralValidationErrorColour,
      DropdownGeneralPlaceholderColour:
        widgetConfiguratorStyles.DropdownInputViewModel
          .GeneralPlaceholderColour,
      DropdownBorderColour:
        widgetConfiguratorStyles.DropdownInputViewModel.BorderColour,
      DropdownBorderFocusColour:
        widgetConfiguratorStyles.DropdownInputViewModel.BorderFocusColour,
      DropdownBorderRadius:
        widgetConfiguratorStyles.DropdownInputViewModel.BorderRadius,
      DropdownSelectedBackgroundColour:
        widgetConfiguratorStyles.DropdownInputViewModel
          .SelectedBackgroundColour,
      DropdownSelectedTextColour:
        widgetConfiguratorStyles.DropdownInputViewModel.SelectedTextColour,
      PromoBoxBorderColour:
        widgetConfiguratorStyles.PromoChoiceViewModel.BoxBorderColour,
      PromoBoxBorderRadius:
        widgetConfiguratorStyles.PromoChoiceViewModel.BoxBorderRadius,
      PromoOptionBackgroundColour:
        widgetConfiguratorStyles.PromoChoiceViewModel.OptionBackgroundColour,
      PromoOptionTextColour:
        widgetConfiguratorStyles.PromoChoiceViewModel.OptionTextColour,
      PromoOptionHoverBackgroundColour:
        widgetConfiguratorStyles.PromoChoiceViewModel
          .OptionHoverBackgroundColour,
      PromoOptionHoverTextColour:
        widgetConfiguratorStyles.PromoChoiceViewModel.OptionHoverTextColour,
      PromoSelectedBackgroundColour:
        widgetConfiguratorStyles.PromoChoiceViewModel.SelectedBackgroundColour,
      PromoSelectedTextColour:
        widgetConfiguratorStyles.PromoChoiceViewModel.SelectedTextColour,
      CustomLogoUrl: widgetConfiguratorStyles.CustomLogoViewModel
        ? widgetConfiguratorStyles.CustomLogoViewModel.RelativePath
        : "",
    };
    setState({
      crossSellSetup: state.crossSellSetup
        ? { ...state.crossSellSetup, ThemeStyles: themeStyles }
        : undefined,
    });
  };

  const currentScreen = (navigateTo: string) => {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    const tomorrowString = today.toISOString().split("T")[0] + "T17:00:00";

    switch (navigateTo) {
      case "InitialViewModel":
        setState({
          currentPage: "SelectDate",
          initialTimeSlot: state.crossSellSetup?.UseNewSkipTimeSelectFlow
            ? { ...state.initialTimeSlot, TimeSlot: { TimeSlot: "" } }
            : state.initialTimeSlot,
        });
        break;
      case "CrossSellVenueSelectionViewModel":
        setState({
          currentPage: "SelectTime",
          availabilityType: state.availabilityType || "partialOutsideTwoHours",
          availabilityResponses: state.availabilityResponses || [
            {
              ...widgetConfiguratorAvailabilitySearchResponses[0],
              MicrositeName: state.micrositeName!,
              Promotions: [],
            },
          ],
          initialTimeSlot: state.initialTimeSlot.TimeSlot.TimeSlot
            ? state.initialTimeSlot
            : {
                ...bookingDetails.timeSlot!,
                TimeSlot: {
                  ...bookingDetails.timeSlot!.TimeSlot,
                  TimeSlot: tomorrowString,
                },
                MicrositeName: state.micrositeName!,
              },
          searchedCrossSellVenues:
            state.searchedCrossSellVenues.length > 0
              ? state.searchedCrossSellVenues
              : state.crossSellSetup!.CrossSellVenues!.map((venue) => {
                  return {
                    CrossSellVenue: venue,
                    AvailabilitySearchResponse: {
                      ...widgetConfiguratorAvailabilitySearchResponses[0],
                      MicrositeName: venue.MicrositeName,
                    },
                  } as CrossSellVenueAndAvailability;
                }),
          availableCrossSellVenues:
            state.availableCrossSellVenues.length > 0
              ? state.availableCrossSellVenues
              : state.crossSellSetup!.CrossSellVenues!,
        });
        break;
      case "PromotionViewModel":
        if (!isLoading) {
          setIsLoading(true);
          if (
            !state.availabilityResponses ||
            state.availabilityResponses?.length === 0
          ) {
            widgetService
              .availabilitySearch(
                state.hostname!,
                state.micrositeName!,
                today,
                2,
                state.channelCode!,
                AvailabilityType.Reservation
              )
              .then((response) => {
                setState({
                  currentPage: "PromotionPage",
                  availabilityResponses: [
                    { ...response, MicrositeName: state.micrositeName! },
                  ],
                  initialTimeSlot: state.initialTimeSlot.TimeSlot.TimeSlot
                    ? state.initialTimeSlot
                    : {
                        ...bookingDetails.timeSlot!,
                        TimeSlot: {
                          ...bookingDetails.timeSlot!.TimeSlot,
                          TimeSlot: tomorrowString,
                        },
                        MicrositeName: state.micrositeName!,
                      },
                  bookingDetails: {
                    ...bookingDetails,
                    selectedDate: today,
                    timeSlot: {
                      TimeSlot: response.TimeSlots[0],
                      MicrositeName: state.micrositeName!,
                    },
                  },
                });
                setIsLoading(false);
              });
          } else {
            setState({
              currentPage: "PromotionPage",
              initialTimeSlot: state.initialTimeSlot.TimeSlot.TimeSlot
                ? state.initialTimeSlot
                : {
                    ...bookingDetails.timeSlot!,
                    TimeSlot: {
                      ...bookingDetails.timeSlot!.TimeSlot,
                      TimeSlot: tomorrowString,
                    },
                    MicrositeName: state.micrositeName!,
                  },
              bookingDetails: {
                ...bookingDetails,
                selectedDate: today,
                timeSlot: {
                  TimeSlot: state.availabilityResponses![0].TimeSlots[0],
                  MicrositeName: state.micrositeName!,
                },
              },
            });
            setIsLoading(false);
          }
        }
        break;
      case "ContactDetailsViewModel":
        setState({
          currentPage: "ContactDetails",
          bookingDetails: {
            ...bookingDetails,
            timeSlot: {
              ...bookingDetails.timeSlot,
              TimeSlot: {
                ...bookingDetails.timeSlot!.TimeSlot,
                TimeSlot: tomorrowString,
              },
            },
            selectedDate: today,
          },
        });
        break;
      default:
        console.log(navigateTo);
        break;
    }
  };

  const flashBookingSummaryPanel = () => {
    // If the current page is not one that contains booking summary, then navigate to the SelectTime page
    if (
      [
        "ContactDetails",
        "ConfirmDetails",
        "ErrorPage",
        "PromotionPage",
        "SelectTime",
      ].indexOf(state.currentPage) == -1
    ) {
      const today = new Date();
      today.setDate(today.getDate() + 1);
      const tomorrowString = today.toISOString().split("T")[0] + "T17:00:00";
      setState({
        currentPage: "SelectTime",
        availabilityType: state.availabilityType || "partialOutsideTwoHours",
        availabilityResponses: state.availabilityResponses || [
          {
            ...widgetConfiguratorAvailabilitySearchResponses[0],
            MicrositeName: state.micrositeName!,
            Promotions: [],
          },
        ],
        initialTimeSlot: state.initialTimeSlot.TimeSlot.TimeSlot
          ? state.initialTimeSlot
          : {
              ...bookingDetails.timeSlot!,
              TimeSlot: {
                ...bookingDetails.timeSlot!.TimeSlot,
                TimeSlot: tomorrowString,
              },
              MicrositeName: state.micrositeName!,
            },
        searchedCrossSellVenues:
          state.searchedCrossSellVenues.length > 0
            ? state.searchedCrossSellVenues
            : state.crossSellSetup!.CrossSellVenues!.map((venue) => {
                return {
                  CrossSellVenue: venue,
                  AvailabilitySearchResponse: {
                    ...widgetConfiguratorAvailabilitySearchResponses[0],
                    MicrositeName: venue.MicrositeName,
                  },
                } as CrossSellVenueAndAvailability;
              }),
        availableCrossSellVenues:
          state.availableCrossSellVenues.length > 0
            ? state.availableCrossSellVenues
            : state.crossSellSetup!.CrossSellVenues!,
        bookingDetails: bookingDetails,
      });
    }

    setState({
      flashBookingSummaryPanel: true,
    });

    window.setTimeout(() => {
      setState({
        flashBookingSummaryPanel: false,
      });
    }, 2000);
  };

  const reloadContactDetailsSettings = (
    contactDetailsSettings: ReloadContactDetails
  ) => {
    setState({
      crossSellSetup: state.crossSellSetup
        ? ({
            ...state.crossSellSetup,
            ThemeSettings: {
              ...state.crossSellSetup.ThemeSettings,
              ShowDateOfBirth: contactDetailsSettings.ShowDateOfBirth,
              ShowDateOfBirthYear: contactDetailsSettings.ShowDateOfBirthYear,
              DateOfBirthRequired: contactDetailsSettings.DateOfBirthRequired,
              DateOfBirthDescription:
                contactDetailsSettings.DateOfBirthDescription,
              ContactDetailsTitle: contactDetailsSettings.ContactDetailsTitle,
              ShowCustomerTitle: contactDetailsSettings.ShowCustomerTitle,
              ShowSmsMarketingOption:
                contactDetailsSettings.ShowSmsMarketingOption,
              ShowOptinCheckboxes: contactDetailsSettings.ShowOptinCheckboxes,
              ShowPostcodeField: contactDetailsSettings.ShowPostcodeField,
              PostcodeRequired: contactDetailsSettings.PostcodeRequired,
              ShowTelephoneNumberField:
                contactDetailsSettings.ShowTelephoneNumberField,
              ShowCompanyField: contactDetailsSettings.ShowCompanyField,
              ShowMembershipIdField:
                contactDetailsSettings.ShowMembershipIdField,
              ShowMobileNumberField:
                contactDetailsSettings.ShowMobileNumberField,
              MarketingLevel: contactDetailsSettings.MarketingLevel,
              ShowRoomNumberField: contactDetailsSettings.ShowRoomNumberField,
              ShowHouseholdsField: contactDetailsSettings.ShowHouseholdsField,
              ShowCommentsField: contactDetailsSettings.ShowCommentsField,
              PrecheckVenueEmailOptin:
                contactDetailsSettings.PrecheckVenueEmailOptin,
            },
          } as CrossSellSetupResponse)
        : undefined,
    });
  };

  const loadUserContactDetails = (userContactDetails: object) => {
    console.log(userContactDetails);
  };

  const reloadBookingSummaryInputSettings = (bookingSummaryInputs: object) => {
    console.log(bookingSummaryInputs);
  };

  const channelCode = (themeSettingsChannelCode: string) => {
    setState({
      channelCode: themeSettingsChannelCode,
    });
  };

  const updateVenues = (groupWidgetRestaurants: object) => {
    console.log(groupWidgetRestaurants);
  };

  const widgetTakeawaySetting = (widgetTakeawaySetting: object) => {
    console.log(widgetTakeawaySetting);
  };

  const addCrossSellVenue = (addCrossSellVenue: string) => {
    if (
      !state.crossSellSetup?.CrossSellVenues?.find(
        (venue) => venue.MicrositeName === addCrossSellVenue
      ) &&
      !isLoading
    ) {
      setIsLoading(true);
      widgetService
        .getRestaurant(state.hostname || "", addCrossSellVenue)
        .then((response) => {
          if (response) {
            const crossSellVenue = {
              Name: response.Name,
              MicrositeName: addCrossSellVenue,
              Address: response.Address.AddressLine,
              City: response.Address.City,
              Latitude: response.Address.Latitude,
              Longitude: response.Address.Longitude,
              Priority: state.crossSellSetup?.CrossSellVenues
                ? state.crossSellSetup.CrossSellVenues.length + 1
                : 1,
            } as CrossSellVenue;
            setState({
              crossSellSetup: state.crossSellSetup
                ? {
                    ...state.crossSellSetup,
                    CrossSellVenues: [
                      ...(state.crossSellSetup.CrossSellVenues || []),
                      crossSellVenue,
                    ],
                  }
                : undefined,
              availableCrossSellVenues: [
                ...(state.availableCrossSellVenues || []),
                crossSellVenue,
              ],
              searchedCrossSellVenues: [
                ...(state.searchedCrossSellVenues || []),
                {
                  CrossSellVenue: crossSellVenue,
                  AvailabilitySearchResponse: {
                    ...widgetConfiguratorAvailabilitySearchResponses[0],
                    MicrositeName: addCrossSellVenue,
                  },
                } as CrossSellVenueAndAvailability,
              ],
            });
            setIsLoading(false);
          }
        });
    }
  };

  const removeCrossSellVenue = (removeCrossSellVenue: string) => {
    const crossSellVenue = state.crossSellSetup?.CrossSellVenues?.find(
      (venue) => venue.MicrositeName === removeCrossSellVenue
    );
    if (crossSellVenue) {
      setState({
        crossSellSetup: state.crossSellSetup
          ? {
              ...state.crossSellSetup,
              CrossSellVenues: state.crossSellSetup.CrossSellVenues?.filter(
                (venue) => venue.MicrositeName !== removeCrossSellVenue
              ),
            }
          : undefined,
        availableCrossSellVenues: state.availableCrossSellVenues?.filter(
          (venue) => venue.MicrositeName !== removeCrossSellVenue
        ),
        searchedCrossSellVenues: state.searchedCrossSellVenues?.filter(
          (venue) => venue.CrossSellVenue.MicrositeName !== removeCrossSellVenue
        ),
      });
    }
  };

  const rearrangeCrossSellVenues = (
    rearrangedCrossSellVenues: Array<string>
  ) => {
    setState({
      searchedCrossSellVenues: state.searchedCrossSellVenues?.sort((a, b) => {
        return (
          rearrangedCrossSellVenues.indexOf(a.CrossSellVenue.MicrositeName) -
          rearrangedCrossSellVenues.indexOf(b.CrossSellVenue.MicrositeName)
        );
      }),
    });
  };

  const [messages, setMessages] = useState<MessageEvent[]>([]);

  const handleWidgetConfiguratorMessage = (event: MessageEvent) => {
    if (
      event.origin.indexOf("resdiary.com") > 0 ||
      event.origin.indexOf("rdbranch.com") > 0 ||
      event.origin.indexOf("cloudapp.net") > 0 ||
      event.origin.indexOf("goodluckwiththerelease.com") > 0 ||
      // Should add an environment variable and only allow this in DEV mode
      event.origin.indexOf("localhost") >= 0
    ) {
      // Skip duplicate messages
      if (messages[messages.length - 1]?.data === event.data) return;
      setMessages((prevState) => {
        return [...prevState, event];
      });
    }
  };

  const processWidgetConfiguratorMessageRef =
    useRef<(event: MessageEvent) => void>();

  processWidgetConfiguratorMessageRef.current = (event: MessageEvent) => {
    if (event.data.css) {
      updateCSS(event.data.css);
    }
    if (event.data.navigateTo) {
      currentScreen(event.data.navigateTo);
    }
    if (event.data.flashBookSummaryPanel) {
      flashBookingSummaryPanel();
    }
    if (event.data.contactDetailsSettings) {
      reloadContactDetailsSettings(event.data.contactDetailsSettings);
    }
    if (event.data.userContactDetails) {
      loadUserContactDetails(event.data.userContactDetails);
    }
    if (event.data.bookingSummaryInputs) {
      reloadBookingSummaryInputSettings(event.data.bookingSummaryInputs);
    }
    if (event.data.themeSettingsChannelCode) {
      channelCode(event.data.themeSettingsChannelCode);
    }
    if (event.data.groupWidgetRestaurants) {
      updateVenues(event.data.groupWidgetRestaurants);
    }
    if (event.data.widgetTakeawaySetting) {
      widgetTakeawaySetting(event.data.widgetTakeawaySetting);
    }
    if (event.data.addCrossSellVenue) {
      addCrossSellVenue(event.data.addCrossSellVenue);
    }
    if (event.data.removeCrossSellVenue) {
      removeCrossSellVenue(event.data.removeCrossSellVenue);
    }
    if (event.data.rearrangeCrossSellVenues) {
      rearrangeCrossSellVenues(event.data.rearrangeCrossSellVenues);
    }
  };

  useEffect(() => {
    const processedMessages = [...messages];
    processedMessages.forEach((message) => {
      if (processWidgetConfiguratorMessageRef.current) {
        processWidgetConfiguratorMessageRef.current(message);
      }
    });
    setMessages((prevState) => {
      return [...prevState.splice(processedMessages.length, prevState.length)];
    });
  }, [messages]);

  if (!isListenerAttached) {
    window.addEventListener("message", handleWidgetConfiguratorMessage);
    setIsListenerAttached(true);
  }

  return null;
};
export default WidgetConfiguratorMessageHandler;
